import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { CoreDrawer } from "@/components/common/CoreDrawer";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useConsolidateDocuments } from "@/hooks/documents";
import { useMergePreview } from "@/hooks/knowledge/useMergePreview";
import { cn } from "@/lib/utils";
import type { TopicCluster } from "@/types/knowledge/knowledge-brain";
import type { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

interface RewriteBrainDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  clusters: TopicCluster[];
  documents: DocumentWithMetadata[];
  /** Cluster to open on, when the user arrived by clicking one. */
  initialClusterId?: string | null;
}

/**
 * A front door to the consolidation the app already has, driven by topic
 * instead of multi-select. The proposed merged document is shown against its
 * sources BEFORE anything is written, and the prune-originals option stays off
 * unless the user turns it on.
 *
 * The bulk-select path in `BulkActionToolbar` is untouched and still works.
 */
const RewriteBrainDrawer = ({
  isOpen,
  onClose,
  clusters,
  documents,
  initialClusterId,
}: RewriteBrainDrawerProps) => {
  const mergeableClusters = useMemo(
    () => clusters.filter((cluster) => cluster.documentCount >= 2),
    [clusters],
  );

  const [clusterId, setClusterId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [documentName, setDocumentName] = useState("");
  const [deleteOriginals, setDeleteOriginals] = useState(false);
  const [step, setStep] = useState<"choose" | "preview">("choose");

  const { mutateAsync: consolidate, isPending: isRewriting } = useConsolidateDocuments();

  const cluster = useMemo(
    () => mergeableClusters.find((c) => c.id === clusterId) ?? null,
    [mergeableClusters, clusterId],
  );

  const clusterDocuments = useMemo(() => {
    if (!cluster) return [];
    const ids = new Set(cluster.documentIds);
    return documents.filter((doc) => ids.has(doc.id));
  }, [cluster, documents]);

  const selectedDocuments = useMemo(
    () => clusterDocuments.filter((doc) => selectedIds.includes(doc.id)),
    [clusterDocuments, selectedIds],
  );

  const preview = useMergePreview(selectedDocuments, isOpen && step === "preview");

  useEffect(() => {
    if (!isOpen) return;
    const opening =
      initialClusterId && mergeableClusters.some((c) => c.id === initialClusterId)
        ? initialClusterId
        : (mergeableClusters[0]?.id ?? null);
    setClusterId(opening);
    setStep("choose");
    setDeleteOriginals(false);
  }, [isOpen, initialClusterId, mergeableClusters]);

  useEffect(() => {
    setSelectedIds(clusterDocuments.map((doc) => doc.id));
    setDocumentName(cluster ? cluster.label : "");
  }, [cluster?.id, clusterDocuments.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handleAccept = async () => {
    if (selectedIds.length < 2) return;
    try {
      await consolidate({
        documentIds: selectedIds,
        docName: documentName.trim() || undefined,
        prune: deleteOriginals,
      });
      onClose();
    } catch {
      // useConsolidateDocuments surfaces its own error toast.
    }
  };

  return (
    <CoreDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Rewrite the brain"
      description="Fold everything you hold on a topic into one clean document your Genies can lean on."
      className="bg-[#F9FAFC]"
    >
      <div className="space-y-6 py-6">
        {mergeableClusters.length === 0 ? (
          <p className="rounded-xl border border-dashed border-gray-300 px-4 py-10 text-center text-sm text-gray-500">
            Rewriting needs at least two sources on the same topic. Add more knowledge first.
          </p>
        ) : (
          <>
            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Pick a topic to rewrite
              </label>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {mergeableClusters.map((option) => {
                  const active = option.id === clusterId;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => {
                        setClusterId(option.id);
                        setStep("choose");
                      }}
                      className={cn(
                        "rounded-xl border-2 p-4 text-left transition-all",
                        active
                          ? "border-genie-primary bg-genie-primary/5"
                          : "border-gray-200 bg-white hover:border-gray-300",
                      )}
                    >
                      <p className="text-sm font-semibold text-gray-900">{option.label}</p>
                      <p className="mt-1 text-xs text-gray-500">
                        {option.documentCount} sources
                        {option.staleCount > 0 ? ` · ${option.staleCount} stale` : ""}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {cluster && (
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <label className="text-sm font-semibold text-gray-700">Sources to fold in</label>
                  <span className="text-xs text-gray-500">{selectedIds.length} selected</span>
                </div>
                <div className="max-h-64 space-y-2 overflow-y-auto pr-1">
                  {clusterDocuments.map((doc) => {
                    const active = selectedIds.includes(doc.id);
                    return (
                      <button
                        key={doc.id}
                        type="button"
                        onClick={() => toggle(doc.id)}
                        className={cn(
                          "flex w-full items-center gap-3 rounded-xl border-2 p-3 text-left transition-all",
                          active
                            ? "border-genie-primary bg-genie-primary/5"
                            : "border-gray-200 bg-white hover:border-gray-300",
                        )}
                      >
                        <Checkbox checked={active} className="pointer-events-none shrink-0" />
                        <span className="truncate text-sm text-gray-800">{doc.name}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-700">
                Name the rewritten document
              </label>
              <Input
                value={documentName}
                onChange={(event) => setDocumentName(event.target.value)}
                placeholder="e.g. Membership policies"
                className="h-11"
              />
            </div>

            {step === "preview" && (
              <div className="rounded-2xl border border-gray-200 bg-white p-5">
                <h3 className="text-sm font-semibold text-gray-900">Proposed document</h3>
                <p className="mt-1 text-xs text-gray-500">
                  Read this against the sources it came from before you accept it.
                </p>

                {preview.isLoading ? (
                  <div className="flex items-center gap-3 py-8 text-sm text-gray-500">
                    <Loader2 className="h-5 w-5 animate-spin text-genie-primary" />
                    Assembling the preview…
                  </div>
                ) : preview.isError ? (
                  <p className="mt-4 text-sm text-red-600">
                    Could not build the preview. Try again, or rewrite from the document list.
                  </p>
                ) : (
                  <>
                    <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <div>
                        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500">
                          Sources
                        </p>
                        <ul className="space-y-2">
                          {preview.data?.sources.map((source) => (
                            <li
                              key={source.documentId}
                              className="rounded-xl border border-gray-200 p-3"
                            >
                              <p className="truncate text-sm font-medium text-gray-900">
                                {source.name}
                              </p>
                              <p className="mt-0.5 text-xs text-gray-500">
                                {source.error
                                  ? source.error
                                  : `${source.characters.toLocaleString()} characters${
                                      source.truncated ? " (preview shortened)" : ""
                                    }`}
                              </p>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500">
                          Merged result
                        </p>
                        <pre className="max-h-80 overflow-auto whitespace-pre-wrap rounded-xl border border-gray-200 bg-gray-50 p-3 font-sans text-xs leading-relaxed text-gray-700">
                          {preview.data?.merged || "No readable content in the selected sources."}
                        </pre>
                      </div>
                    </div>

                    {preview.data?.truncated && (
                      <p className="mt-3 flex items-start gap-2 text-xs text-gray-500">
                        <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-genie-accent" />
                        Long sources are shortened in this preview. The rewrite keeps every word,
                        and section order can differ slightly from the order shown here.
                      </p>
                    )}
                  </>
                )}

                <label className="mt-5 flex items-start gap-2.5 text-sm text-gray-700">
                  <Checkbox
                    checked={deleteOriginals}
                    onCheckedChange={(value) => setDeleteOriginals(value === true)}
                    className="mt-0.5"
                  />
                  <span>
                    Remove the original sources afterwards
                    <span className="mt-0.5 block text-xs text-gray-500">
                      Off by default — keep them until you have read the rewrite.
                    </span>
                  </span>
                </label>
              </div>
            )}

            <div className="flex flex-wrap gap-3 border-t border-gray-200 pt-4">
              <Button variant="outline" onClick={onClose} disabled={isRewriting}>
                Discard
              </Button>
              {step === "choose" ? (
                <Button
                  onClick={() => setStep("preview")}
                  disabled={selectedIds.length < 2}
                  className="bg-gradient-to-r from-genie-primary to-genie-secondary px-6 font-semibold text-white shadow-lg shadow-genie-primary/30 hover:from-genie-primary-hover hover:to-genie-secondary disabled:opacity-50"
                >
                  Preview the rewrite
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setStep("choose")} disabled={isRewriting}>
                    Edit the selection
                  </Button>
                  <Button
                    onClick={handleAccept}
                    disabled={isRewriting || selectedIds.length < 2}
                    className="bg-gradient-to-r from-genie-primary to-genie-secondary px-6 font-semibold text-white shadow-lg shadow-genie-primary/30 hover:from-genie-primary-hover hover:to-genie-secondary disabled:opacity-50"
                  >
                    {isRewriting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Accept and rewrite
                  </Button>
                </>
              )}
              {selectedIds.length < 2 && (
                <p className="flex w-full items-center gap-1.5 text-xs text-gray-500">
                  <AlertCircle className="h-3.5 w-3.5" />
                  Pick at least two sources to rewrite.
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </CoreDrawer>
  );
};

export default RewriteBrainDrawer;
